import { ScrollView } from '@ipfabric/ui-components/ScrollView/ScrollView';
import { useClickAway } from '@ipfabric/ui-components/hooks/useClickAway';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import { ESCAPE } from '@ipfabric/ui-components/utils/keyboard/constants';
import type { GraphViewPreviewable } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { deleteViews } from 'components/Graph/commands/deleteViews';
import { loadView } from 'components/Graph/commands/loadView';
import { starView } from 'components/Graph/commands/starView';
import { unstarView } from 'components/Graph/commands/unstarView';
import { graphScreenshotToDataUrl } from 'components/Graph/helpers/graphScreenshot';
import { useLocale } from 'containers/App/LocaleContext';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import { useEffect, useState } from 'react';
import type { FC, RefObject } from 'react';
import { useConfirmDialog } from 'utils/hooks/useConfirmDialog';
import { useGraphContextSelectors } from '../GraphContext';
import { GraphLoadViewField } from './GraphLoadViewField';
import { GraphLoadViewItem } from './GraphLoadViewItem';
import { useViews } from './useViews';

export const DELETE_VIEW_CONFIRM = 'deleteViewConfirmDialog';

interface Props {
  onClose: () => void;
  wrapperRef: RefObject<HTMLDivElement>;
}

export const GraphLoadViewDropdown: FC<Props> = (props) => {
  const [searchedName, setSearchedName] = useState<string>('');
  const [viewToDelete, setViewToDelete] = useState<GraphViewPreviewable | null>(
    null,
  );
  const [deleting, setDeleting] = useState<boolean>(false);
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    snapshotId: prop('snapshotId'),
    views: prop('loadViewDialog', 'views'),
    viewIdsBeingStarred: prop('loadViewDialog', 'viewIdsBeingStarred'),
    viewIdsBeingUnstarred: prop('loadViewDialog', 'viewIdsBeingUnstarred'),
  });
  const [preview, setPreview] = useState<string | null>(null);
  const views = useViews(ctxProps.views, searchedName);
  const globalStateCtxProps = useGlobalStateContextSelectors({
    authId: prop('auth', 'id'),
  });
  const msg = useLocale();
  const deleteViewConfirm = useConfirmDialog({ name: DELETE_VIEW_CONFIRM });

  const isViewByUser = (view: GraphViewPreviewable) =>
    !!(view.userId && view.userId === globalStateCtxProps.authId);

  const handleDeleteClick = useStableCallback((view: GraphViewPreviewable) => {
    setViewToDelete(view);
    deleteViewConfirm.toggleConfirmDialog();
  });

  const handleDeleteViewConfirm = async () => {
    if (viewToDelete) {
      setDeleting(true);
      await ctxProps.dispatch(deleteViews, { views: [viewToDelete] });
      setDeleting(false);
      setViewToDelete(null);
    }
  };

  const handleStarredToggle = useStableCallback(
    (view: GraphViewPreviewable) => {
      ctxProps.dispatch(view.favorite ? unstarView : starView, { view });
    },
  );

  const handleViewClick = useStableCallback((view: GraphViewPreviewable) => {
    ctxProps.dispatch(loadView, { view });
    props.onClose();
  });

  const handleKeyDown = useStableCallback((event: KeyboardEvent) => {
    if (event.which === ESCAPE) {
      props.onClose();
    }
  });

  useEffect(() => {
    const eventListeners = new ManagedDomEventListeners([
      document,
      'keydown',
      handleKeyDown,
    ]);

    return () => {
      eventListeners.remove();
    };
  }, []);

  useClickAway<HTMLDivElement>(() => {
    // Value in viewToDelete means there is confirmation dialog open. While confirm dialog uses portal it's not
    // considered part of this component so we have to disable closing on outside click manually
    if (!viewToDelete) {
      props.onClose();
    }
  }, props.wrapperRef);

  return (
    <>
      <ConfirmDialog
        id={DELETE_VIEW_CONFIRM}
        msg={msg.canvasGraph.commands.loadView.deleteConfirm}
        onClickConfirm={handleDeleteViewConfirm}
        onCloseClick={() => {
          setViewToDelete(null);
        }}
        btnConfirmVariant='primary'
        btnConfirmAccent='negative'
        btnCancelVariant='secondary'
        btnCancelAccent='positive'
      />
      {preview && (
        <div className='graph-load-view__preview'>
          <img
            className='max-h-full max-w-full object-contain'
            alt={msg.canvasGraph.commands.loadView.preview}
            src={graphScreenshotToDataUrl(preview)}
          />
        </div>
      )}
      <div className='graph-load-view__dropdown'>
        <GraphLoadViewField setValue={setSearchedName} value={searchedName} />
        <ScrollView
          className='graph-load-view__scrollbars'
          contentClassName='flex flex-col flex-1'
        >
          {searchedName.length > 0 && views.length === 0 ? (
            <div className='py-lgu-3 px-lgu-6'>
              {msg.canvasGraph.commands.loadView.noResultsFound}
            </div>
          ) : (
            <div>
              {views.map((view) => (
                <GraphLoadViewItem
                  deleting={deleting && viewToDelete?.id === view.id}
                  favorite={view.favorite}
                  isViewByUser={isViewByUser}
                  key={view.id}
                  onDeleteClick={handleDeleteClick}
                  onStarredToggle={handleStarredToggle}
                  onViewClick={handleViewClick}
                  snapshotId={ctxProps.snapshotId}
                  starToggling={
                    ctxProps.viewIdsBeingStarred.has(view.id) ||
                    ctxProps.viewIdsBeingUnstarred.has(view.id)
                  }
                  view={view}
                  setPreview={setPreview}
                />
              ))}
            </div>
          )}
        </ScrollView>
      </div>
    </>
  );
};
